import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { KeyRound, ExternalLink, Loader2, ShieldCheck, Eye, EyeOff, LogIn } from "lucide-react";
import { authorize } from "@/lib/deriv";
import { useAuth } from "@/hooks/useAuth";
import { useConfig } from "@/hooks/useConfig";
import { generatePkce } from "@/lib/pkce";
import { api } from "@/lib/api";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";
import { toast } from "sonner";

const ConnectTokenDialog = ({ open, onOpenChange, onConnected }) => {
  const { login } = useAuth();
  const { config } = useConfig();
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  // Kicks off Deriv's NEW OAuth 2.0 + PKCE flow per their 2026 docs
  // (auth.deriv.com/oauth2/auth). After the user signs in, Deriv redirects
  // back to /auth/callback with `?code=...`, AuthCallback.jsx exchanges
  // the code for an access token and stores a session. The PAT-like access
  // token is then used by /api/deriv/otp to fetch a markup-attributed
  // WebSocket URL — every trade earns us the 3% set on developers.deriv.com.
  const handleOAuthLogin = async () => {
    setOauthLoading(true);
    try {
      const redirectUri = `${window.location.origin.replace("://www.", "://")}/auth/callback`;
      const clientId = config?.deriv_oauth_client_id || config?.deriv_app_id;
      if (!clientId) throw new Error("Deriv client_id not configured");
      const { challenge, state } = await generatePkce();
      const authUrl = new URL(
        config?.deriv_auth_url || "https://auth.deriv.com/oauth2/auth",
      );
      authUrl.searchParams.set("response_type", "code");
      authUrl.searchParams.set("client_id", clientId);
      authUrl.searchParams.set("redirect_uri", redirectUri);
      authUrl.searchParams.set("scope", "trade account_manage");
      authUrl.searchParams.set("state", state);
      authUrl.searchParams.set("code_challenge", challenge);
      authUrl.searchParams.set("code_challenge_method", "S256");
      window.location.href = authUrl.toString();
    } catch (e) {
      setOauthLoading(false);
      toast.error("Couldn't start Deriv login", { description: e?.message || String(e) });
    }
  };

  // Deriv tokens must match ^[\w\-]{1,128}$ — strip anything else (whitespace,
  // newlines, smart-quotes inserted by iOS Safari clipboard, etc.)
  const sanitize = (raw) => (raw || "").replace(/[^\w-]/g, "");

  const handleConnect = async () => {
    const t = sanitize(token);
    if (!t || t.length < 8) {
      toast.error("That doesn't look like a Deriv API token", {
        description: "Paste the full token (starts with `a1-` and is ~30 chars long).",
      });
      return;
    }
    setSubmitting(true);

    // Legacy tokens from app.deriv.com/account/api-token (format like
    // "N2tYFw7PE1gZFQJ") work on Deriv's legacy WebSocket but Deriv refuses
    // to credit our 3% markup on those trades — confirmed via testing. Only
    // modern `pat_...` tokens (from developers.deriv.com) work with the new
    // REST API that routes through our App ID. Refuse the legacy format and
    // push users to the OAuth flow.
    const isModernPat = /^pat_/i.test(t);
    if (!isModernPat) {
      toast.error("This token format isn't supported", {
        description:
          "Legacy API tokens trade through Deriv's generic app and aren't fully supported here. Please use 'Login with Deriv' (OAuth) instead — recommended for the best experience.",
        duration: 10000,
      });
      setSubmitting(false);
      return;
    }

    // ─── NEW FLOW (per developers.deriv.com docs) ────────────────────────
    // Step A: GET /trading/v1/options/accounts (via our backend proxy that
    //         adds the Deriv-App-ID header) — lists accounts for this token.
    // Step B: POST /trading/v1/options/accounts/{id}/otp → returns the
    //         authenticated WebSocket URL.
    // Step C: Save session with connection_type='otp' + the ws_url.
    // If any step fails we fall back to the legacy WS authorize call (works
    // for older PAT tokens but does not credit the 3% markup).
    try {
      // ── Step A ──
      const accountsRes = await api.get("/deriv/accounts", {
        headers: { Authorization: `Bearer ${t}` },
        validateStatus: () => true,
      });

      let newFlowOk = false;
      let chosenAccount = null;
      if (accountsRes.status >= 200 && accountsRes.status < 300) {
        const payload = accountsRes.data?.data || accountsRes.data || {};
        const list = payload.accounts || payload || [];
        const arr = Array.isArray(list) ? list : [];
        if (arr.length > 0) {
          // Prefer real (non-virtual) over demo
          chosenAccount =
            arr.find((a) => (a.account_type || a.type) === "real") ||
            arr.find((a) => !(a.is_virtual || a.virtual)) ||
            arr[0];
          newFlowOk = true;
        }
      }

      if (newFlowOk && chosenAccount) {
        const accountId =
          chosenAccount.id ||
          chosenAccount.account_id ||
          chosenAccount.loginid;
        const currency = chosenAccount.currency || "USD";

        // ── Step B ──
        const otpRes = await api.post(
          `/deriv/otp/${encodeURIComponent(accountId)}`,
          {},
          {
            headers: { Authorization: `Bearer ${t}` },
            validateStatus: () => true,
          }
        );
        const otpData = otpRes.data?.data || otpRes.data || {};
        const wsUrl = otpData.url || otpData.ws_url || otpData.websocket_url;

        if (otpRes.status >= 200 && otpRes.status < 300 && wsUrl) {
          // ── Step C — success: store the authenticated session
          const session = {
            token: t,
            account_id: accountId,
            currency,
            ws_url: wsUrl,
            connection_type: "otp",
            markup_ready: true,
          };
          login(session);
          api
            .post("/track/login", {
              account_id: accountId,
              currency,
              user_agent: navigator.userAgent,
            })
            .catch(() => {});
          toast.success(`Connected as ${accountId}`, {
            description: `${currency} · Markup attribution ENABLED`,
          });
          setSubmitting(false);
          onOpenChange(false);
          setToken("");
          onConnected?.(session);
          return;
        }
      }

      // ─── FALLBACK: legacy WS authorize ──────────────────────────────────
      const res = await authorize(t);
      if (res?.error) {
        const code = res.error.code || "";
        let msg = res.error.message || "Deriv rejected the token.";
        if (code === "InputValidationFailed") {
          msg = "Token contains invalid characters. Try pasting again — make sure you copied the FULL token only.";
        } else if (code === "InvalidToken") {
          msg = "Deriv says this token doesn't exist or has expired. Generate a new one with Read + Trade scopes.";
        }
        toast.error("Couldn't connect", { description: msg });
        setSubmitting(false);
        return;
      }
      const auth = res.authorize;
      const accountId = auth?.loginid || "UNKNOWN";
      const currency = auth?.currency || "USD";
      const accounts = (auth?.account_list || []).map((a) => ({
        token: t,
        account_id: a.loginid,
        currency: a.currency,
      }));
      const session = {
        token: t,
        account_id: accountId,
        currency,
        accounts: accounts.length ? accounts : [{ token: t, account_id: accountId, currency }],
        connection_type: "api_token",
        markup_ready: false,
      };
      login(session);
      api
        .post("/track/login", {
          account_id: accountId,
          currency,
          user_agent: navigator.userAgent,
        })
        .catch(() => {});
      toast.success(`Connected as ${accountId}`, {
        description: `${auth?.balance ?? 0} ${currency} · Connected via legacy mode`,
        duration: 6000,
      });
      setSubmitting(false);
      onOpenChange(false);
      setToken("");
      onConnected?.(session);
    } catch (e) {
      setSubmitting(false);
      toast.error("Connection error", { description: e?.message || String(e) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="connect-token-dialog"
        className="bg-vts-bg border-vts-line text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <LogIn className="h-5 w-5 text-vts-blue" />
            Connect your Deriv account
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <p className="text-sm text-vts-silver/80 font-body leading-relaxed">
            Sign in with your normal Deriv email and password — no API tokens,
            no copy-paste. We're a registered Deriv partner; your trades stay
            on your Deriv account.
          </p>

          {/* PRIMARY — Deriv OAuth */}
          <Button
            data-testid="connect-oauth-btn"
            onClick={handleOAuthLogin}
            disabled={oauthLoading || submitting}
            className="w-full h-12 bg-[#ff444f] hover:bg-[#e63b45] text-white font-semibold text-sm shadow-lg shadow-[#ff444f]/20"
          >
            {oauthLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Redirecting to Deriv…
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4 mr-2" />
                Login with Deriv
              </>
            )}
          </Button>

          <div className="flex items-center gap-2 text-[11px] text-vts-silver/60 font-body">
            <ShieldCheck className="h-3.5 w-3.5 text-vts-profit shrink-0" />
            Official Deriv OAuth · markup attribution enabled · 30-second setup
          </div>

          {/* NO DERIV ACCOUNT YET — affiliate signup so the referral is attributed */}
          <a
            data-testid="connect-signup-affiliate-link"
            href={DERIV_AFFILIATE_URL}
            target="_blank"
            rel="noreferrer"
            className="block w-full rounded-lg border border-vts-line bg-vts-surface/50 px-4 py-3 text-center text-sm font-semibold text-white hover:border-vts-blue hover:bg-vts-blue/10 transition-colors"
          >
            New to Deriv? Create a free account →
            <span className="block text-[10px] font-normal text-vts-silver/55 mt-0.5">
              Takes 2 minutes — then come back and tap Login with Deriv
            </span>
          </a>

          {/* ADVANCED — API Token (DISABLED).
              Legacy API tokens can't earn markup because Deriv's legacy
              WebSocket doesn't accept our alphanumeric app_id and falls back
              to the generic app_id=1089 (zero markup). Hiding the entire
              path to protect partner revenue — OAuth is the only allowed
              connection method. Code kept for future re-enable if Deriv
              ever surfaces numeric app_ids. */}
          {false && (
          <>
          {/* Divider */}
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-vts-line"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-vts-bg px-2 text-[10px] uppercase tracking-wider text-vts-silver/50 font-mono">
                or advanced
              </span>
            </div>
          </div>

          {/* ADVANCED — API Token (collapsible) */}
          <details data-testid="api-token-section" className="group border border-vts-line rounded-md overflow-hidden">
            <summary className="cursor-pointer select-none px-3 py-2 flex items-center justify-between hover:bg-vts-surface/40 transition">
              <div className="flex items-center gap-2">
                <KeyRound className="h-3.5 w-3.5 text-vts-silver/60" />
                <span className="text-xs text-vts-silver/80 font-medium">Use API token instead</span>
                <span className="text-[10px] text-vts-silver/40 font-mono">(power users)</span>
              </div>
              <span className="text-vts-silver/50 text-[10px] group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="px-3 pb-3 pt-2 space-y-3">
          <div className="bg-vts-loss/10 border border-vts-loss/30 rounded-md p-3">
            <div className="text-[11px] text-vts-loss font-semibold mb-1">
              ⚠️ Only modern PAT tokens earn markup
            </div>
            <p className="text-[11px] text-vts-silver/80 leading-relaxed">
              Legacy API tokens from app.deriv.com (format like{" "}
              <code className="text-vts-silver/60">N2tYFw7PE1gZFQJ</code>) trade through
              Deriv's generic app — <strong className="text-vts-loss">0% commission to you</strong>.
              Only tokens starting with{" "}
              <code className="text-vts-blue">pat_</code> from{" "}
              <a
                data-testid="open-deriv-token-page"
                href="https://developers.deriv.com/dashboard"
                target="_blank"
                rel="noreferrer"
                className="text-vts-blue hover:text-vts-blueHover inline-flex items-center gap-1 underline underline-offset-2"
              >
                developers.deriv.com <ExternalLink className="h-3 w-3" />
              </a>{" "}
              credit you the 3% markup. For end users, OAuth (above) is the easiest path.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
                API Token
              </label>
              <button
                data-testid="api-token-toggle-visibility"
                type="button"
                onClick={() => setShowToken((s) => !s)}
                className="text-[10px] uppercase tracking-wider text-vts-silver/60 hover:text-white flex items-center gap-1 font-mono"
              >
                {showToken ? (
                  <>
                    <EyeOff className="h-3 w-3" /> Hide
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3" /> Show
                  </>
                )}
              </button>
            </div>
            <Input
              data-testid="api-token-input"
              type={showToken ? "text" : "password"}
              placeholder="a1-XXXXXXXXXXXXXXXXXXXXXXXX"
              value={token}
              onChange={(e) => setToken(sanitize(e.target.value))}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = (e.clipboardData || window.clipboardData).getData("text");
                setToken(sanitize(pasted));
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className="bg-vts-surface border-vts-line text-white font-mono tabular-nums h-11 tracking-wider"
            />
            <p className="text-[10px] text-vts-silver/50 mt-1.5 font-mono">
              Length: {token.length} chars{token.length > 0 && token.length < 20 && " — too short"}
            </p>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              data-testid="api-token-cancel-btn"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="flex-1 border-vts-line bg-vts-surface hover:bg-vts-surfaceHover text-white"
            >
              Cancel
            </Button>
            <Button
              data-testid="api-token-connect-btn"
              onClick={handleConnect}
              disabled={submitting || !token.trim()}
              className="flex-1 bg-vts-blue hover:bg-vts-blueHover text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting…
                </>
              ) : (
                "Connect with token"
              )}
            </Button>
          </div>
            </div>
          </details>
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectTokenDialog;
